import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { debugLogger } from '@/utils/debugLogger';
import { toast } from 'sonner';
import { blockService } from '@/core/services/BlockService';

interface BlockState {
  isBlocked: boolean;
  isBlockedBy: boolean;
  isLoading: boolean;
}

export function useBlockUser(targetUserId: string | undefined) {
  const { user } = useAuth();
  const [state, setState] = useState<BlockState>({
    isBlocked: false,
    isBlockedBy: false,
    isLoading: false
  });

  const checkBlockStatus = useCallback(async () => {
    if (!user?.id || !targetUserId) return;

    try {
      const { isBlocked, isBlockedBy } = await blockService.checkBlockStatus(user.id, targetUserId);

      setState(prev => ({
        ...prev,
        isBlocked,
        isBlockedBy
      }));
    } catch (error) {
      debugLogger.error('[useBlockUser] Error checking block status:', error);
    }
  }, [user?.id, targetUserId]);

  const blockUser = useCallback(async (reason?: string) => {
    if (!user?.id || !targetUserId) return false;

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      // 1. Insert block record
      const { error: blockError } = await supabase
        .from('user_blocks')
        .insert({
          blocker_id: user.id,
          blocked_id: targetUserId,
          reason
        });

      if (blockError) throw blockError;

      // 2. Remove existing connection (both directions)
      const { error: connectionError } = await supabase
        .from('connections')
        .delete()
        .or(`and(user_id.eq.${user.id},target_id.eq.${targetUserId}),and(user_id.eq.${targetUserId},target_id.eq.${user.id})`);

      if (connectionError) {
        debugLogger.warn('[useBlockUser] Error removing connection on block:', connectionError);
        // Don't fail the block if connection removal fails
      }

      // 3. Soft-delete any conversation between users
      const { error: convError } = await supabase
        .from('conversations')
        .update({ 
          deleted_at: new Date().toISOString(), 
          deleted_by: user.id 
        })
        .or(`and(user1_id.eq.${user.id},user2_id.eq.${targetUserId}),and(user1_id.eq.${targetUserId},user2_id.eq.${user.id})`)
        .is('deleted_at', null);

      if (convError) {
        debugLogger.warn('[useBlockUser] Error soft-deleting conversation on block:', convError);
        // Don't fail the block if conversation deletion fails
      }

      // 4. Invalidate cache
      blockService.invalidateCache(user.id, targetUserId);

      setState(prev => ({ ...prev, isBlocked: true, isLoading: false }));
      toast.success('Usuário bloqueado com sucesso');
      debugLogger.info('[useBlockUser] User blocked with side effects:', { 
        targetUserId,
        connectionRemoved: !connectionError,
        conversationDeleted: !convError
      });
      return true;
    } catch (error) {
      debugLogger.error('[useBlockUser] Error blocking user:', error);
      toast.error('Erro ao bloquear usuário');
      setState(prev => ({ ...prev, isLoading: false }));
      return false;
    }
  }, [user?.id, targetUserId]);

  const unblockUser = useCallback(async () => {
    if (!user?.id || !targetUserId) return false;

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const { error } = await supabase
        .from('user_blocks')
        .delete()
        .eq('blocker_id', user.id)
        .eq('blocked_id', targetUserId);

      if (error) throw error;

      // Invalidate cache
      blockService.invalidateCache(user.id, targetUserId);

      setState(prev => ({ ...prev, isBlocked: false, isLoading: false }));
      toast.success('Usuário desbloqueado');
      debugLogger.info('[useBlockUser] User unblocked:', { targetUserId });
      return true;
    } catch (error) {
      debugLogger.error('[useBlockUser] Error unblocking user:', error);
      toast.error('Erro ao desbloquear usuário');
      setState(prev => ({ ...prev, isLoading: false }));
      return false;
    }
  }, [user?.id, targetUserId]);

  return {
    ...state,
    checkBlockStatus,
    blockUser,
    unblockUser
  };
}
